import React, { useRef, useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit3, FiTrash2, FiDownload, FiCheck, FiRefreshCw, FiPrinter } = FiIcons;

const WritingPractice = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedChar, setSelectedChar] = useState({ char: 'እኔ', label: 'Ene (I)' });

  const characters = [
    { char: 'እኔ', label: 'Ene (I)' },
    { char: 'እኛ', label: 'Egna (We)' },
    { char: 'እርስዎ', label: 'Erswo (Formal You)' },
    { char: 'ቤት', label: 'Bet (House)' },
    { char: 'ሰላም', label: 'Selam (Peace)' }
  ];

  useEffect(() => {
    clearCanvas();
  }, [selectedChar]);

  // Helper to get precise coordinates relative to the canvas scale
  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);

    if (clientX === undefined || clientY === undefined) return { x: 0, y: 0 };

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);

    ctx.lineWidth = 12; // Thicker line for better visibility
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round'; // Smoother corners
    ctx.strokeStyle = '#4F46E5'; // Indigo color
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath(); // Reset path to prevent connecting separate strokes
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <PageLayout
      title="Writing Practice"
      subtitle="Practice writing Amharic pronouns. Trace on screen or print worksheets for the classroom."
      icon={FiEdit3}
      color="text-blue-600"
      bgColor="bg-blue-50"
      prevLink="/teacher-hub"
      prevLabel="Teacher Hub"
    >
      <div className="grid lg:grid-cols-3 gap-8 fade-in">
        {/* Sidebar: Character Selection */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 mb-4">Select Word</h3>
            <div className="space-y-2">
              {characters.map((item) => (
                <button
                  key={item.char}
                  onClick={() => setSelectedChar(item)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${selectedChar.char === item.char
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <span className="font-amharic text-xl font-bold">{item.char}</span>
                  <span className="text-sm font-medium opacity-90">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
            <h3 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
              <SafeIcon icon={FiPrinter} /> Printable Worksheets
            </h3>
            <p className="text-sm text-indigo-700 mb-4">
              Download PDF-ready tracing sheets for the entire class.
            </p>
            <button
              onClick={handlePrint}
              className="w-full py-3 bg-white text-indigo-600 border border-indigo-200 rounded-xl font-bold hover:bg-indigo-600 hover:text-white transition-all flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiDownload} /> Print Current Sheet
            </button>
          </div>
        </div>

        {/* Main: Drawing Canvas */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-200 relative">
            {/* Toolbar */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Trace: {selectedChar.label}</h2>
              <button
                onClick={clearCanvas}
                className="flex items-center gap-2 px-4 py-2 text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-sm font-bold"
              >
                <SafeIcon icon={FiTrash2} /> Clear Board
              </button>
            </div>

            {/* Canvas Container */}
            <div className="relative w-full aspect-[4/3] bg-gray-50 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 cursor-crosshair touch-none">
              {/* Background Template */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
                <span className="font-amharic text-[12rem] text-gray-200 font-bold opacity-50">
                  {selectedChar.char}
                </span>
              </div>
              
              {/* Grid Lines */}
              <div className="absolute inset-x-0 top-1/2 h-px bg-blue-200/50 pointer-events-none"></div>
              <div className="absolute inset-y-0 left-1/2 w-px bg-blue-200/50 pointer-events-none"></div>

              {/* Canvas Element with touch-none to prevent scrolling */}
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                className="w-full h-full relative z-10 touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <p className="text-center text-gray-400 text-sm mt-4">
              Use your mouse or finger to trace over the gray letter.
            </p>
          </div>
        </div>
      </div>

      {/* Print Only Section - Hidden in normal view */}
      <div className="hidden print:block fixed inset-0 bg-white z-[9999] p-8">
        <div className="text-center border-b-2 border-gray-800 pb-4 mb-8">
          <h1 className="text-4xl font-bold mb-2">Amharic Writing Practice</h1>
          <p className="text-xl">Name: __________________________ Date: _____________</p>
        </div>
        <div className="grid grid-rows-4 gap-8">
          {[1, 2, 3, 4].map(row => (
            <div key={row} className="flex items-center gap-4 border-b border-gray-300 pb-4">
              <div className="w-24 h-24 flex items-center justify-center font-amharic text-6xl text-black font-bold border-r border-gray-300 pr-4">
                {selectedChar.char}
              </div>
              <div className="flex-1 flex gap-8">
                {[1, 2, 3, 4, 5].map(col => (
                  <div key={col} className="flex-1 h-24 border-b-2 border-dashed border-gray-300 relative">
                    <span className="absolute inset-0 flex items-center justify-center font-amharic text-5xl text-gray-200">
                      {selectedChar.char}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center text-gray-500">
          <p>www.bkamharic.com - Social Grammar Learning</p>
        </div>
      </div>
    </PageLayout>
  );
};

export default WritingPractice;